import React, { useState, useMemo, useCallback } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { X } from "lucide-react";
import Loader from "../../../Loader";
import { useAccountClassification } from "../../hooks/useAccountClassification";
import { COLORS } from "../../utils/constants";
import ClassificationKPICard from "../KPICard/ClassificationKPICard";
// import ClassificationModal from "../Modal/ClassificationModal";
import ReusableTable from "../ReusableTable/ReusableTable.jsx"
const AccClsDonutChart = ({ width = 600, height = 353 }) => {
  const { 
    classificationData, 
    rawData, 
    totalAccounts, 
    loading, 
    error 
  } = useAccountClassification();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const handleCardClick = useCallback((status) => {
    setSelectedStatus(status);
    setIsModalOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedStatus(null);
  }, []);
  const kpiCards = useMemo(() => {
    const statusMap = new Map(classificationData.map((d) => [d.name, d.value]));
    return [
      { name: "Healthy", value: statusMap.get("Healthy") || 0 },
      { name: "At Risk", value: statusMap.get("At Risk") || 0 },
      { name: "Inactive", value: statusMap.get("Inactive") || 0 },
    ];
  }, [classificationData]);
  const filteredData = useMemo(
    () =>
      selectedStatus
        ? rawData.filter((org) => org.status === selectedStatus)
        : rawData,
    [rawData, selectedStatus]
  );
  const totalWidth = width;
  const totalHeight = height;
  if (loading) {
    return (
      <div
        className="flex items-center justify-center"
        style={{
          width: totalWidth,
          height: totalHeight,
          position: "relative",
        }}
      >
        <Loader />
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="text-[#D3B4FF] p-4"
        style={{
          width: totalWidth,
          height: totalHeight,
        }}
      >
        Error: {error}
      </div>
    );
  }
  return (
    <div
      className="flex items-start"
      style={{
        width: totalWidth,
        height: totalHeight,
        position: "relative",
      }}
    >
      {/* Donut Chart Container */}
      <div
        className="glass-effect p-4 rounded-2xl border border-yellow-500/30"
        style={{
          width: 400,
          height,
          borderRadius: "6px",
          background:
            "linear-gradient(252.82deg, rgba(217, 217, 217, 0.025) 25.41%, rgba(115, 108, 207, 0.005) 115.2%)",
          backdropFilter: "blur(75px)",
          border: "0px solid",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div className="flex justify-between items-center mb-2">
          <h3
            className="text-lg font-medium text-[#D3B4FF]"
            style={{
              fontFamily: "Inter",
              fontWeight: 300,
              fontStyle: "Light",
              fontSize: "16px",
              lineHeight: "100%",
              letterSpacing: "1%",
              opacity: 1,
              transform: "rotate(0deg)",
              whiteSpace: "nowrap",
              marginTop: "7px",
              marginLeft: "10px",
            }}
          >
            Account Classification
          </h3>
        </div>
        <div
          className="rounded-xl p-1 flex items-center justify-center"
          style={{ color: "#D3B4FF" }}
        >
          <PieChart width={360} height={300}>
            <Pie
              data={classificationData}
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={120}
              fill="#8884d8"
              dataKey="value"
              stroke="none"
              onClick={(entry) => handleCardClick(entry.name)}
            >
              {classificationData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                  stroke="none"
                />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                border: "none",
                borderRadius: "4px",
                color: "white",
              }}
              itemStyle={{ color: "white" }}
            />
            <text
              x="50%"
              y="50%"
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#D3B4FF"
            >
              <tspan x="50%" dy="-0.5em" fontSize="28" fontWeight="normal">
                {totalAccounts}
              </tspan>
              <tspan x="50%" dy="1.2em" fontSize="15" fontWeight="normal">
                Total
              </tspan>
              <tspan x="50%" dy="1.2em" fontSize="15" fontWeight="normal">
                Accounts
              </tspan>
            </text>
          </PieChart>
        </div>
      </div>
      {/* KPI Cards Container */}
      <div className="flex flex-col gap-3 ml-4">
        {kpiCards.map((entry, index) => (
          <ClassificationKPICard
            key={index}
            entry={entry}
            totalAccounts={totalAccounts}
            onClick={handleCardClick}
            index={index}
          />
        ))}
      </div>

      {/* Modal for Classification Data */}
      {isModalOpen && (
        <div
      className="fixed inset-0 flex items-center justify-center z-50 overflow-hidden"
      onClick={closeModal}
    >
      <div
        className="fixed inset-0 bg-black bg-opacity-50"
        style={{
          backdropFilter: "blur(100px)",
          WebkitBackdropFilter: "blur(18px)",
        }}
      />
      <div
        className="w-full max-w-5xl p-6 rounded-lg shadow-lg relative max-h-[80vh] overflow-y-auto"
        style={{
          background:
            "linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))",
          backdropFilter: "blur(40px)",
          WebkitBackdropFilter: "blur(50px)",
          borderRadius: "10px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-normal text-[#D3B4FF]">
            {selectedStatus
              ? `${selectedStatus} Account Classification Data`
              : "Account Classification Data"}
          </h2>
          <button
            onClick={closeModal}
            className="text-[#CCCCCC] hover:text-[#D3B4FF] transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-[200px] text-[#D3B4FF]">
            <Loader />
          </div>
        ) : error ? (
          <div className="text-center py-8 text-[#CCCCCC]">
            Error: {error}
          </div>
        ) : !filteredData || filteredData.length === 0 ? (
          <div className="text-center py-8 text-[#CCCCCC]">
            No {selectedStatus ? `${selectedStatus.toLowerCase()} ` : ""}
            classification data available.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <ReusableTable
              columns={[
                {
                  key: "organization_name",
                  label: "Organization",
                  sortable: true,
                  width: "20%",
                  render: (value) => (
                    <span className="font-medium">{value || "N/A"}</span>
                  ),
                },
                {
                  key: "status",
                  label: "Status",
                  sortable: true,
                  width: "10%",
                  render: (status) => (
                    <span
                      className={`font-medium ${
                        status === "Healthy"
                          ? "text-green-400"
                          : status === "At Risk"
                          ? "text-yellow-400"
                          : "text-red-400"
                      }`}
                    >
                      {status || "Unknown"}
                    </span>
                  ),
                },
                {
                  key: "account_type",
                  label: "Account Type",
                  sortable: true,
                  width: "10%",
                  render: (value) => value || "Unknown",
                },
                {
                  key: "reason",
                  label: "Reason",
                  sortable: false,
                  width: "60%",
                  render: (value) => (
                    <span className="text-[#CCCCCC]">
                      {value || "No reason provided"}
                    </span>
                  ),
                },
              ]}
              data={filteredData}
              visibleColumns={{
                organization_name: true,
                status: true,
                account_type: true,
                reason: true,
              }}
              noDataMessage="No organizations found matching the current filters."
              truncateLength={30}
              className="min-w-[800px]"
              rowClassName="hover:bg-[#5A4479]/10 transition-colors"
            />
          </div>
        )}
      </div>
    </div>
      )}
    </div>
  );
};

export default AccClsDonutChart;